/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import {getDistance} from 'geolib';
import {GeolibInputCoordinates} from 'geolib/es/types';

/**
  * 
  * Calculates the distance between two geo coordinates.
  * 
  * This function takes up to 3 arguments. First 2 arguments must be valid `GeolibInputCoordinates` 
  * (e.g. `{latitude: 52.518611, longitude: 13.408056}`). Coordinates can be in sexagesimal or decimal format. 
  * The third argument is accuracy (in meters). By default the accuracy is 1 meter. 
  * If you need a more accurate result, you can set it to a lower value, e.g. to `0.01` for centimeter accuracy. 
  * You can set it higher to have the result rounded to the next value that is divisible 
  * by your chosen accuracy (e.g. `25428` with an accuracy of `100` becomes `25400`).
  * 
  * ```
  *     const distance = getDistanceByCoordinates(
  *         { latitude: 52.518611, longitude: 13.408056 },
  *         { latitude: 51.519475, longitude: 7.46694444 }
  *     )
  * 
  *     const distance = getDistanceByCoordinates(
  *         { latitude: 52.518611, longitude: 13.408056 },
  *         { latitude: 51.519475, longitude: 7.46694444 },
  *         100
  *     )
  * 
  *     const distance = getDistanceByCoordinates(
  *         { latitude: 51.5103, longitude: 7.49347 },
  *         { latitude: "51° 31' N", longitude: "7° 28' E" }
  *     )
  *```
  * 
  */
export const getDistanceByCoordinates = (
  from: GeolibInputCoordinates,
  to: GeolibInputCoordinates,
  accuracy: number = 1
): number => {
  return getDistance(
    from,
    to,
    accuracy
  );
};
